import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Solutions from './components/Solutions';
import Services from './components/Services';
import CTA from './components/CTA';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title
    document.title = 'Boca Raton Business Turnaround Revivacorp';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Revivacorp provides business turnaround and restructuring consulting services in Boca Raton, helping corporations overcome financial challenges and achieve sustainable growth through strategic guidance.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', siteUrl);
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Boca Raton Business Turnaround Revivacorp' },
      { property: 'og:description', content: 'Revivacorp provides business turnaround and restructuring consulting services in Boca Raton, helping corporations overcome financial challenges and achieve sustainable growth through strategic guidance.' },
      { property: 'og:url', content: siteUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: `${siteUrl}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Boca Raton Business Turnaround Revivacorp' },
      { name: 'twitter:description', content: 'Revivacorp provides business turnaround and restructuring consulting services in Boca Raton, helping corporations overcome financial challenges and achieve sustainable growth through strategic guidance.' },
      { name: 'twitter:image', content: `${siteUrl}/og-image.jpg` }
    ];
    
    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Revivacorp',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: 'Business turnaround and restructuring consulting services helping corporations overcome financial challenges and achieve sustainable growth',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2255 Glades Rd',
        addressLocality: 'Boca Raton',
        addressRegion: 'FL',
        postalCode: '33431',
        addressCountry: 'US'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'info@revivacorp.com',
        contactType: 'Customer Service'
      },
      sameAs: []
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Revivacorp',
      url: siteUrl,
      description: 'Business turnaround and restructuring consulting services',
      publisher: {
        '@type': 'Organization',
        name: 'Revivacorp'
      }
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(organizationSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(websiteSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      <Hero />
      <Solutions />
      <Services />
      <CTA />
      <Footer />
    </div>
  );
}